import React from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

import './design/tokens.css';
import './design/base.css';
import './design/nav.css';
import './design/hero.css';
import './design/sections.css';
import './design/contact.css';
import './design/gallery.css';
import './design/about.css';
import './design/partnership.css';
import './design/monitoring.css';
import './design/demo.css';

import { AnimationProvider } from './ui/Motion';
import Nav from './sections/Nav';
import Footer from './sections/Footer';
import ScrollManager from './sections/ScrollManager';
import ScrollProgress from './ui/ScrollProgress';

/* Imported eagerly, not with React.lazy.
 *
 * Every one of these routes is prerendered to static HTML at build time.
 * A lazy route would hand React a Suspense fallback on first paint while
 * its chunk downloaded, so the reader would watch real content flash back
 * to a spinner. Eager imports cost a larger main bundle and buy an
 * instant takeover plus instant navigation between routes. */
import Home from './pages/home';

import ResidentialServices from './pages/residential-services';
import CommercialServices from './pages/commercial-services';
import ElectricalMaintenance from './pages/electrical-maintenance';
import PowerSystems from './pages/power-systems';
import LightingSolutions from './pages/lighting-solutions';
import EnergyEfficiency from './pages/energy-efficiency';
import SafetyStandards from './pages/safety-standards';
import Services from './pages/services';
import Portfolio from './pages/portfolio';
import About from './pages/about';
import Tpi from './pages/tpi';
import DataCenterMonitoring from './pages/data-center-monitoring';
import MonitoringDemo from './pages/monitoring-demo';
import { DEMO_PATH } from './demo/screens';

import PrivacyPolicy from './pages/privacy-policy';
import NotFound from './pages/404';

/* The SmartDC dashboard is an application with a frame of its own, so the site's
   nav and footer step aside on its routes. The grain stays: it is part of how
   every page on the site looks. */
function Shell({ children }) {
    const { pathname } = useLocation();
    const isApplication = pathname === DEMO_PATH || pathname.startsWith(`${DEMO_PATH}/`);

    if (isApplication) {
        return (
            <>
                <main>{children}</main>
                <div className="grain" aria-hidden="true" />
            </>
        );
    }

    return (
        <>
            <ScrollManager />
            <ScrollProgress />
            <Nav />
            <main>{children}</main>
            <Footer />
            <div className="grain" aria-hidden="true" />
        </>
    );
}

export default function App() {
    return (
        <HelmetProvider>
            <AnimationProvider>
                <Router>
                    <Shell>
                        <Routes>
                            <Route path="/" element={<Home />} />
                            <Route path="/residential-services" element={<ResidentialServices />} />
                            <Route path="/commercial-services" element={<CommercialServices />} />
                            <Route path="/electrical-maintenance" element={<ElectricalMaintenance />} />
                            <Route path="/power-systems" element={<PowerSystems />} />
                            <Route path="/lighting-solutions" element={<LightingSolutions />} />
                            <Route path="/energy-efficiency" element={<EnergyEfficiency />} />
                            <Route path="/safety-standards" element={<SafetyStandards />} />
                            <Route path="/services" element={<Services />} />
                            <Route path="/portfolio" element={<Portfolio />} />
                                {/* Backs up the server-side 301 in .htaccess for any
                                    stale client-side link. */}
                            <Route path="/gallery" element={<Navigate to="/portfolio" replace />} />
                            <Route path="/about" element={<About />} />
                            <Route path="/tpi" element={<Tpi />} />
                            <Route path="/data-center-monitoring" element={<DataCenterMonitoring />} />
                            <Route path="/data-center-monitoring/demo" element={<MonitoringDemo />} />
                            <Route path="/data-center-monitoring/demo/:view" element={<MonitoringDemo />} />
                            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                            <Route path="*" element={<NotFound />} />
                        </Routes>
                    </Shell>
                </Router>
            </AnimationProvider>
        </HelmetProvider>
    );
}
